import React from 'react'
import { useNavigation } from '@react-navigation/native'
import { View } from 'react-native'
import { Product } from '@src/types.ts'
import {
  ProductSummary,
  ProductSummaryProps,
} from '@screens/Marketplace/components/NewMarketplace/ProductSummary.tsx'
import { openUrl } from '@utils'
import { useSelectedAddonModal } from '@screens/Marketplace/utils/hooks.ts'
import { usePreloadSteloAddonProducts } from '@screens/Marketplace/utils/hooks/useSteloCgmProducts'
import { cgmSteloDoubleImage, nutritionistHubNutritionCoachingImage } from '@assets/images'
import { StyleService, useStyleSheet } from '@src/style/service'
import { useLazyScreenPreload } from '@src/navigation/utils'
import { Analytics, CustomEventTypes } from '@config'
import { StressManagementProductSummary } from '../StressManagement/StressManagementProductSummary'
import { LabPanelsProductSummary } from '../LabPanels/LabPanelsProductSummary'

interface ProductsGridV2Props {
  products: Product[]
}

const centeredImageStyles = { alignSelf: 'center' } as const

export const ProductsGrid = ({ products }: ProductsGridV2Props) => {
  const navigation = useNavigation()
  const selectedAddonModal = useSelectedAddonModal()
  const styles = useStyleSheet(themedStyle)
  const addons = products.filter((product) => product.addon)
  useLazyScreenPreload('StressManagementIntroScreen')
  usePreloadSteloAddonProducts(addons)

  const onProductPress = (product: Product) => {
    if (product.url) {
      openUrl(product.url)
      return
    }

    if (product.dietitian) {
      navigation.navigate('SubscriptionCheckout', { product })
      return
    }

    if (product.addon && !product.dietitianMenu) {
      selectedAddonModal(product)
      return
    }

    navigation.navigate('DietitianMenuCheckout', {
      selection: { product },
      goBackOnSuccess: true,
    })
  }

  const getAddonImageProps = (
    product: Product,
  ): Pick<ProductSummaryProps, 'image' | 'imageStyle'> => {
    if (product.cgm) {
      return {
        image: cgmSteloDoubleImage,
        imageStyle: { ...centeredImageStyles, paddingHorizontal: 8 },
      }
    }

    if (product.dietitian) {
      return {
        image: nutritionistHubNutritionCoachingImage,
        imageStyle: { ...centeredImageStyles, maxWidth: 110, paddingHorizontal: 4 },
      }
    }

    return { image: undefined }
  }

  const onLabPanelsPress = () => {
    Analytics.track(CustomEventTypes.FullscriptLabsCardClicked)
    navigation.navigate('LabPanelsScreen')
  }

  return (
    <View style={styles.container}>
      <StressManagementProductSummary
        onPress={() => navigation.navigate('StressManagementIntroScreen', { source: 'addons' })}
        showPrice
        skipLoadingSchedulingInfo
      />
      <LabPanelsProductSummary onPress={onLabPanelsPress} />
      {products.map((product) => {
        return (
          <ProductSummary
            key={product.key}
            title={product.title}
            description={product.shortDescription || product.description || ''}
            callout={product.callout}
            onPress={() => onProductPress(product)}
            price={product.price}
            showUnitPrice={product.showUnitPrice}
            units={product.units}
            isCgm={product.cgm}
            interval={product.interval}
            {...getAddonImageProps(product)}
          />
        )
      })}
    </View>
  )
}

const themedStyle = StyleService.create({
  container: {
    gap: 12,
  },
})
